import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

export interface UseBenchmarkingDataResult {
  records: Record<string, any>[];
  loading: boolean;
  error: string | null;
}

/**
 * Fetches benchmarking data (ACS_BPS_metrics_demo) for a given place + FIPS.
 * Handles loading and error states and re-fetches when inputs change.
 */
export const useBenchmarkingData = (
  place: string | null,
  placeFips: string | null
): UseBenchmarkingDataResult => {
  const [records, setRecords] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // If inputs are missing, reset.
    if (!place || !placeFips) {
      setRecords([]);
      setError(null);
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('ACS_BPS_metrics_demo')
        .select('*')
        .eq('Place', place)
        .eq('place_fips', placeFips)
        .order('year', { ascending: false });

      if (error) {
        console.error('Supabase fetch error for benchmarking data:', error);
        setError(
          error.message ||
            'Failed to fetch benchmarking data. See console for details.'
        );
        setRecords([]);
      } else {
        setRecords(data ?? []);
      }

      setLoading(false);
    };

    fetchData();
  }, [place, placeFips]);

  return { records, loading, error };
}; 